import {
  UseMutationOptions,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { minutes } from '@utils/milliseconds';

import { useWebSocket, waitForElasticRefresh } from 'api/hooks';
import { useInvalidateJourneyActionForms } from 'api/journey-action-forms/hooks';
import { handleError, handlePossibleNonFieldError } from 'api/query-client';
import {
  BASE_ACTION_TYPE,
  JourneyAction,
  NonFieldError,
  TravellerJourney,
  TravellerJourneyAction,
  TravellerJourneyActionDetailed,
} from 'api/types';

import {
  createTravellerActionRelationship,
  listTravellerActionRelationships,
  loadJourneyTravellerAction,
  loadTravellerActions,
  bypassJourneyTravellerAction,
  removeTravellerActionRelationship,
  resetActionsFromHere,
  runJourneyTravellerAction,
  skipJourneyTravellerAction,
  unskipJourneyTravellerAction,
  updateJourneyTravellerAction,
} from './requests';

export const getTravellerJourneyActionsQueryKey = (
  journeyTravellerId?: TravellerJourney['shortId']
) => ['traveller-actions', journeyTravellerId].filter(Boolean);

export const useTravellerJourneyActions = (
  journeyTravellerId: string,
  options?: UseQueryOptions<TravellerJourneyAction[]>
) => {
  return useQuery({
    queryKey: getTravellerJourneyActionsQueryKey(journeyTravellerId),
    queryFn: () => loadTravellerActions(journeyTravellerId),
    onSuccess: (actions) => {
      options?.onSuccess?.(actions);
    },
    // Since we use this hook in multiple places on the traveller journey detail for skipping,
    // we're always fetching the actions again and again for no reason.
    // This list is usually updated anyway whenever the user gets a websocket messge
    // that the journey updated so we shouldn't need to refetch the actions so much.
    // I only set the staleTime to 5 minutes just in case the client didn't get notified
    // at some point, otherwise the time could be longer.
    // But I also set the cacheTime to 0 seconds so if the user leaves the detail page,
    // the query data is wiped from the cache.
    // I do this because since the user isn't observing this traveller anymore,
    // they wouldn't invalidate the cache for this query and it would stay stale until
    // the staleTime is reached, even if they re-open the detail page. -@siggirh
    staleTime: minutes(5),
    cacheTime: 0,
  });
};

export const useTravellerActionRelationships = (shortId: string) => {
  return useQuery({
    queryKey: ['travellers', 'actions', 'relationships', shortId],
    queryFn: () => listTravellerActionRelationships(shortId),
  });
};

export const useCreateTravellerActionRelationship = (
  options?: UseMutationOptions<JourneyAction, any, { parentShortId: string; childShortId: string }>
) => {
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(locale);
  return useMutation({
    ...options,
    mutationFn: (data) =>
      createTravellerActionRelationship({
        parentAction: data.parentShortId,
        childAction: data.childShortId,
      }),
    onSuccess: (...data) => {
      queryClient.invalidateQueries(['travellers', 'actions']);
      queryClient.invalidateQueries(['traveller']);
      options?.onSuccess?.(...data);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('travellerActionNotSaved'));
    },
  });
};

export const useRemoveTravellerActionRelationship = (
  options?: UseMutationOptions<unknown, any, string>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (shortId) => removeTravellerActionRelationship(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['travellers', 'actions']);
      queryClient.invalidateQueries(['traveller']);
    },
  });
};

export const getJourneyTravellerActionQueryKey = (
  shortId?: TravellerJourneyActionDetailed['shortId']
) => ['traveller-action', shortId].filter(Boolean);

export const useInvalidateJourneyTravellerActions = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyTravellerAction = (
    shortId?: TravellerJourneyActionDetailed['shortId']
  ) => {
    return queryClient.invalidateQueries(getJourneyTravellerActionQueryKey(shortId));
  };

  return { invalidateJourneyTravellerAction };
};

export const useJourneyTravellerAction = <ActionType extends BASE_ACTION_TYPE>(
  id?: string,
  options?: { onSuccess?: (data: TravellerJourneyActionDetailed<ActionType>) => void }
) => {
  const isEnabled = Boolean(id) && id !== 'undefined';
  const query = useQuery({
    queryKey: getJourneyTravellerActionQueryKey(id),
    queryFn: () => loadJourneyTravellerAction<ActionType>(id!),
    ...options,
    enabled: isEnabled,
  });
  const queryClient = useQueryClient();
  useWebSocket(
    `traveller-action/${id}/`,
    () => queryClient.refetchQueries(['traveller-action', id]),
    { enabled: isEnabled }
  );
  return query;
};

export const useRunJourneyTravellerAction = (
  action: TravellerJourneyActionDetailed,
  options?: UseMutationOptions<unknown, NonFieldError, any>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();

  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: () => runJourneyTravellerAction(action.shortId),
    onSettled: () => {
      queryClient.invalidateQueries(['travellers', action.journeyId]);
      queryClient.invalidateQueries(['traveller', action.travellerJourneyId]);
      invalidateJourneyActionFormsList();
    },
    onSuccess: (...data) => {
      const successText =
        action.action === 'journey_start' ? t('journeyInitated') : t('travellerActionRun');
      showSuccess(successText);
      options?.onSuccess?.(...data);
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('travellerActionNotRun'));
    },
  });
};

export const useUpdateJourneyTravellerAction = (
  travellerAction: TravellerJourneyActionDetailed,
  options?: UseMutationOptions<unknown, NonFieldError, any>
) => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => updateJourneyTravellerAction(travellerAction.shortId, data),
    onSuccess: (...data) => {
      queryClient.invalidateQueries(
        getTravellerJourneyActionsQueryKey(travellerAction.travellerJourneyId)
      );
      queryClient.invalidateQueries(['traveller-action', travellerAction.shortId]);
      queryClient.invalidateQueries(['travellers', travellerAction.journeyId]);
      queryClient.invalidateQueries(['traveller', travellerAction.travellerJourneyId]);
      options?.onSuccess?.(...data);
      showSuccess(t('travellerActionEdited'));
    },
    onError: () => {
      handleError(t('travellerActionNotEdited'));
    },
  });
};

export const useUnskipJourneyTravellerAction = (
  actionId: TravellerJourneyActionDetailed['shortId']
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unskipJourneyTravellerAction(actionId),
    onSettled: () => {
      queryClient.invalidateQueries(['traveller-action', actionId]);
      queryClient.invalidateQueries(['traveller-actions']);
      queryClient.invalidateQueries(['traveller']);
      invalidateJourneyActionFormsList();
    },
    onError: () => {
      handleError(t('actionUnskipError'));
    },
  });
};

export const useSkipJourneyTravellerAction = (
  action_id: TravellerJourneyActionDetailed['shortId']
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => skipJourneyTravellerAction(action_id),
    onSettled: () => {
      queryClient.invalidateQueries(['traveller-action', action_id]);
      queryClient.invalidateQueries(['traveller-actions']);
      queryClient.invalidateQueries(['traveller']);
      invalidateJourneyActionFormsList();
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers']);
      });
    },
    onError: () => {
      handleError(t('actionSkipError'));
    },
  });
};

export const useBypassTravellerAction = (actionId: TravellerJourneyActionDetailed['shortId']) => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => bypassJourneyTravellerAction(actionId),
    onSettled: () => {
      queryClient.invalidateQueries(['traveller-action', actionId]);
      queryClient.invalidateQueries(['traveller-actions']);
      queryClient.invalidateQueries(['traveller']);
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers']);
      });
    },
    onSuccess: () => {
      showSuccess(t('conditionOverridden'));
    },
    onError: () => {
      handleError(t('conditionOverrideError'));
    },
  });
};

export const useResetFromAction = (action_id: TravellerJourneyActionDetailed['shortId']) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateJourneyActionFormsList } = useInvalidateJourneyActionForms();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => resetActionsFromHere(action_id),
    onSettled: () => {
      queryClient.invalidateQueries(['traveller-action', action_id]);
      queryClient.invalidateQueries(['traveller-actions']);
      queryClient.invalidateQueries(['traveller']);
      invalidateJourneyActionFormsList();
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['travellers']);
      });
    },
    onError: () => {
      handleError(t('resetActionsFromHereError'));
    },
  });
};

const locale = {
  en: {
    travellerActionRun: 'Traveller action will be run',
    travellerActionNotRun: 'Traveller action could not run',
    travellerActionEdited: 'Traveller action has been edited',
    travellerActionNotEdited: 'Traveller action could not be updated',
    journeyInitated: 'Journey initiated!',
    travellerActionNotSaved: 'Journey action not saved',
    actionUnskipError: 'Action could not be unskipped',
    actionSkipError: 'Action could not be skipped',
    resetActionsFromHereError: 'Actions could not be reset from here',
    conditionOverridden: 'Condition bypassed',
    conditionOverrideError: 'Could not bypass condition',
  },
};
